import React, { useEffect, useRef, useState } from 'react';
import { approachData } from '../../data/mock';

const ApproachSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="approach" ref={sectionRef} className="relative bg-[#050505] py-24 md:py-32 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1642790106117-e829e14a795f?q=80&w=2940&auto=format&fit=crop"
          alt="Crypto Trading"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]" />
      </div>
      
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div 
          className="mb-16 max-w-4xl"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <p className="text-white/30 text-xs font-medium tracking-[0.3em] mb-6">
            {approachData.sectionLabel}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight tracking-tight mb-6">
            {approachData.title}
          </h2>
          <p className="text-white/60 text-lg font-light leading-relaxed mb-4">
            {approachData.intro}
          </p>
          <p className="text-white/40 text-base font-light leading-relaxed">
            {approachData.subtitle}
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {approachData.pillars.map((pillar, index) => (
            <div
              key={pillar.id}
              className="group relative"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
                transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${200 + index * 100}ms`
              }}
            >
              {/* Card Glow */}
              <div className="absolute -inset-0.5 bg-gradient-to-b from-amber-500/10 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-sm" />
              
              {/* Card */}
              <div className="relative h-full bg-black/60 backdrop-blur-sm border border-white/[0.06] rounded-lg p-8 group-hover:border-white/15 transition-all duration-700">
                {/* Number */}
                <span className="text-amber-500/30 text-5xl font-extralight block mb-4">
                  0{pillar.id}
                </span>
                
                {/* Title */}
                <h3 className="text-xl font-light text-white mb-4 tracking-tight">
                  {pillar.title}
                </h3>
                
                {/* Description */}
                <p className="text-white/50 text-sm font-light leading-relaxed mb-6">
                  {pillar.description}
                </p>
                
                {/* Points */}
                <ul className="space-y-2">
                  {pillar.points.map((point) => (
                    <li key={point} className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-amber-500/50 rounded-full" />
                      <span className="text-white/40 text-xs font-light">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Philosophy */}
        <div 
          className="max-w-3xl mx-auto text-center"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 800ms'
          }}
        >
          <p className="text-white/30 text-xs font-medium tracking-[0.3em] mb-6">
            [ OUR PHILOSOPHY ]
          </p>
          <div className="flex flex-wrap justify-center gap-8 mb-8">
            {approachData.philosophy.points.map((point) => (
              <p key={point} className="text-xl md:text-2xl font-light text-white/70">
                {point}
              </p>
            ))}
          </div>
          <p className="text-white/40 text-base font-light leading-relaxed">
            {approachData.philosophy.note}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;
